import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import type { AppRole } from '@/components/RouteGuard';

interface ViewAsContextValue {
  viewAsRole: AppRole | null;
  setViewAsRole: (role: AppRole | null) => void;
  isViewingAs: boolean;
  actualRoles: AppRole[];
  isActualAdmin: boolean;
}

const ViewAsContext = createContext<ViewAsContextValue | undefined>(undefined);

export function ViewAsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [viewAsRole, setViewAsRole] = useState<AppRole | null>(null);
  const [actualRoles, setActualRoles] = useState<AppRole[]>([]);

  useEffect(() => {
    if (!user) {
      setActualRoles([]);
      setViewAsRole(null);
      return;
    }

    const fetchActualRoles = async () => {
      const { data } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user.id);
      
      const roles = (data ?? []).map(r => r.role as AppRole);
      setActualRoles(roles);
      
      // Reset viewAsRole if user is not admin anymore
      if (!roles.includes('admin')) {
        setViewAsRole(null);
      }
    };

    fetchActualRoles();
  }, [user]);

  const isActualAdmin = actualRoles.includes('admin');
  const isViewingAs = isActualAdmin && viewAsRole !== null;

  return (
    <ViewAsContext.Provider
      value={{
        viewAsRole,
        setViewAsRole,
        isViewingAs,
        actualRoles,
        isActualAdmin,
      }}
    >
      {children}
    </ViewAsContext.Provider>
  );
}

export function useViewAs() {
  const context = useContext(ViewAsContext);
  if (context === undefined) {
    throw new Error('useViewAs must be used within a ViewAsProvider');
  }
  return context;
}
